import React, { useCallback, useEffect, useRef, useState } from 'react';
import { listUnits } from '../../../../api/hierarchy-client';
import { UnitListItem } from '../../../../interfaces/hierarchy-interfaces';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useUnitAllowance } from '../../../hooks/use-unit-allowance';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import {
  Presence,
  Staleable,
  StateSwitch,
  listItemMotion,
} from '../../../../motion';
import { DashboardUnitForm } from '../dashboard-units-components/dashboard-unit-form';
import {
  KindPill,
  OccupancyPill,
  readError,
} from '../dashboard-units-components/units-shared';
import {
  Banner,
  Empty,
  EmptyText,
  EmptyTitle,
  GhostButton,
  RowActions,
  Table,
  TableWrap,
  Tbody,
  Td,
  TdRight,
  Th,
  Thead,
  ThRight,
  Tr,
  UnitName,
  UnitNameEmpty,
  UnitNumber,
} from '../dashboard-units-components/dashboard-units.styled';
import {
  UnitCell,
  UnitRowButton,
  UnitsSection,
  UnitsSectionHead,
  UnitsSectionNote,
  UnitsSectionTitle,
} from './dashboard-buildings.styled';

/**
 * The units of ONE building, inside that building's edit modal.
 *
 * Editing a unit opens the existing DashboardUnitForm IN PLACE — a dialog on
 * top of the building dialog — rather than navigating to /dashboard/units. The
 * two layers share WP-06's dialog stack, so one Escape closes the unit form and
 * leaves the building modal (and any unsaved building edits) exactly as it was.
 * That ordering only holds because the unit form is opened from a STATE FLIP:
 * it mounts in a later commit than the modal around it, which is what puts it
 * on top of the stack. Never render it on this component's first render.
 *
 * PERMISSIONS. The caller mounts this only with `unit:read` (the same gate the
 * server puts on GET /buildings/:id/units, units.js:311). Per-row Edit needs
 * `unit:edit`, Add needs `unit:create` — both are re-checked server-side; these
 * are affordances, not enforcement.
 *
 * THE PREPAID POOL. Add is the ordinary POST /buildings/:id/units, which calls
 * assertUnitAllowance(orgId, 1) BEFORE any insert (units.js:421). No new mint
 * path is introduced here and nothing assumes an unlimited pool: `capped` and
 * `units_remaining` are read from the server through useUnitAllowance, which is
 * the single source of truth (org_unit_allowance()). When WP-12 makes that
 * guard fail closed, an org that reads as uncapped today starts reporting
 * capped-with-0 and this section follows it without a change.
 */

interface BuildingUnitsSectionProps {
  // Numeric building id. The caller derives it from the ?edit=<id> query param
  // (see dashboard-buildings.tsx), which is why a refresh still lists units.
  buildingId: number;
  buildingName: string;
  // The buildings list behind this modal shows a units_count computed
  // server-side, so it goes stale the moment a unit is added, archived, or
  // reclassified as/from a placeholder (properties.js:295-317 counts
  // status='active' AND kind <> 'unassigned'). The caller decides WHEN to act
  // on this; see its own comment for why it waits.
  onUnitsChanged?: () => void;
}

export const BuildingUnitsSection: React.FC<BuildingUnitsSectionProps> = ({
  buildingId,
  buildingName,
  onUnitsChanged,
}) => {
  const { permissions }: { permissions: string[] } = useAuth();
  const canEditUnit = can(permissions, PERMISSIONS.UNIT_EDIT);
  const canCreateUnit = can(permissions, PERMISSIONS.UNIT_CREATE);

  const [units, setUnits] = useState<UnitListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formUnit, setFormUnit] = useState<UnitListItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const loadRef = useRef(0);

  // The org's prepaid pool, read from the ONE endpoint that reports it. Never
  // derived from `units` — a per-building list cannot know what the account has
  // bought, and the two would go stale independently.
  const allowance = useUnitAllowance();
  const outOfUnits = allowance.capped && allowance.remaining <= 0;
  // Only ever shown for a capped account: on an uncapped one the three counts
  // are null, which is NOT the same as zero and must not render as "0 of 0".
  //
  // "Account pool:" is load-bearing copy. This figure is ORG-WIDE and it sits on
  // the same line as this building's own count, inside a modal titled with this
  // building's name — so a second building spending from the same pool, which
  // touches no row of the first, read as the second having taken units off the
  // first. The label is the fix; no number here changes and nothing becomes
  // per-building.
  const poolLine =
    allowance.capped &&
    allowance.allowance &&
    allowance.allowance.units_allowed !== null
      ? ` · Account pool: ${allowance.allowance.units_used} of ${allowance.allowance.units_allowed} units used`
      : '';

  // Sequenced: the modal is keyed on the building id so switching buildings
  // remounts this, but a re-open during an in-flight request would otherwise
  // let the older answer land last.
  const load = useCallback(() => {
    const request = ++loadRef.current;

    setLoading(true);
    setError('');
    listUnits(buildingId)
      .then(result => {
        if (request === loadRef.current) {
          setUnits(result);
        }
      })
      .catch(err => {
        if (request === loadRef.current) {
          setUnits([]);
          setError(
            readError(err, 'Could not load the units in this building.')
          );
        }
      })
      .finally(() => {
        if (request === loadRef.current) {
          setLoading(false);
        }
      });
  }, [buildingId]);

  useEffect(() => {
    load();

    return () => {
      loadRef.current += 1;
    };
  }, [load]);

  const openCreate = () => {
    setFormUnit(null);
    setFormOpen(true);
  };

  const openEdit = (unit: UnitListItem) => {
    setFormUnit(unit);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormUnit(null);
  };

  // Closes the UNIT form only. The building modal is this component's parent and
  // nothing here dismisses it — which is the whole point of editing in place.
  const handleSaved = () => {
    closeForm();
    load();
    // A create spends one unit from the pool and an archive returns one, so the
    // "N left" line has to be re-read rather than decremented locally.
    void allowance.reload();
    onUnitsChanged?.();
  };

  /* "No name" only says something when something else on this list HAS a
     name. See the sub-label itself for the full reasoning (MR-30). */
  const someUnitIsNamed = units.some(u => !!u.name);

  return (
    <UnitsSection aria-label={`Units in ${buildingName}`}>
      <UnitsSectionHead>
        <UnitsSectionTitle>Units</UnitsSectionTitle>
        {canCreateUnit && (
          <GhostButton
            type="button"
            onClick={openCreate}
            disabled={outOfUnits}
            aria-describedby={outOfUnits ? 'building-units-note' : undefined}
          >
            + Add unit
          </GhostButton>
        )}
      </UnitsSectionHead>

      {/* Two different numbers, deliberately labelled apart: how many doors are
          in THIS building (the list), and how much of the account's prepaid
          pool is left (the server). Neither is derived from the other. The
          building's own count says "in this building" rather than "here", so the
          two halves of this line can never be read as one figure. The building
          NAME is deliberately not interpolated: it is arbitrary user text and
          this note has no overflow-wrap floor of its own. */}
      <UnitsSectionNote id="building-units-note">
        {loading
          ? 'Loading units…'
          : `${units.length} ${
              units.length === 1 ? 'unit' : 'units'
            } in this building`}
        {poolLine}
        {outOfUnits
          ? allowance.canBuy
            ? ' — buy more units in Billing to add another.'
            : ' — ask the account owner to buy more units.'
          : ''}
      </UnitsSectionNote>

      {error && <Banner tone="alert">{error}</Banner>}

      {/* The failure branch renders NOTHING below the banner. An empty `units`
          after a failed request is "we do not know", and both alternatives lie:
          the empty state would claim the building has no units, and the table
          would show a header over nothing. */}
      <StateSwitch
        state={
          loading && units.length === 0
            ? 'loading'
            : error
            ? 'error'
            : units.length === 0
            ? 'empty'
            : 'ready'
        }
        minHeight={96}
      >
      {loading && units.length === 0 ? (
        <ListSkeleton rows={3} label="units" />
      ) : error ? null : units.length === 0 ? (
        <Empty>
          <EmptyTitle>No units yet</EmptyTitle>
          <EmptyText>
            {canCreateUnit
              ? `Add the first unit in ${buildingName} to start tracking who lives where.`
              : 'No units have been added to this building yet.'}
          </EmptyText>
        </Empty>
      ) : (
        <Staleable stale={loading}>
        <TableWrap>
          <Table>
            <Thead>
              <tr>
                <Th>Unit</Th>
                <Th>Kind</Th>
                <Th>Occupancy</Th>
                {canEditUnit && <ThRight>Actions</ThRight>}
              </tr>
            </Thead>
            <Tbody>
              {units.map((unit, index) => (
                // No `clickable`, no role and no row handler: the full units
                // screen owns the unit detail page, and this section exists so
                // the user never leaves the building they are editing.
                <Tr key={unit.id} {...listItemMotion(index)} layout={false}>
                  <UnitCell data-label="Unit">
                    <UnitNumber>{unit.unit_number}</UnitNumber>
                    {/* WP-04's treatment, not a second placeholder: an unnamed
                        unit has to read the same here as in the units list. */}
                    {/* MR-30, THE HALF THAT WAS OPEN. units.name is nullable and
                        migration 033 documents NULL as the normal state ("NULL
                        means just the number"), so an unnamed unit is not a
                        defect — but the BULK paths never write one: the allocate
                        endpoint and signup provisioning both insert without it.
                        A customer who provisions in bulk got a wall of "No name"
                        down the column, saying nothing on any row.

                        The sub-label only means something when it
                        DISTINGUISHES — when some units carry a name and this one
                        does not. When none of them do it is noise, so it is not
                        drawn. Open question 3's own recommendation.

                        A JSX comment cannot share a ternary branch with the
                        element it describes, which is why it sits here. */}
                    {unit.name ? (
                      <UnitName>{unit.name}</UnitName>
                    ) : someUnitIsNamed ? (
                      <UnitNameEmpty>No name</UnitNameEmpty>
                    ) : null}
                  </UnitCell>
                  <Td data-label="Kind">
                    <KindPill kind={unit.kind} />
                  </Td>
                  <Td data-label="Occupancy">
                    <OccupancyPill state={unit} />
                  </Td>
                  {canEditUnit && (
                    <TdRight data-label="Actions">
                      {/* The row is inert here, so this guard changes nothing
                          today. It is kept because the units list it was copied
                          from DOES navigate on row click, and a row handler
                          added later must not fire from the Edit button. */}
                      <RowActions onClick={event => event.stopPropagation()}>
                        <UnitRowButton type="button" onClick={() => openEdit(unit)}>
                          Edit
                        </UnitRowButton>
                      </RowActions>
                    </TdRight>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrap>
        </Staleable>
      )}
      </StateSwitch>

      {/* Mounted UNCHANGED — its props are already exactly this shape. It
          portals to <body>, so it is not nested inside the building form's
          <form> and its own <form> is legal. Keyed inside a Presence so it
          animates out on close. */}
      <Presence>
        {formOpen ? (
          <DashboardUnitForm
            key="unit-form"
            buildingId={buildingId}
            unit={formUnit}
            onClose={closeForm}
            onSaved={handleSaved}
          />
        ) : null}
      </Presence>
    </UnitsSection>
  );
};
